import { Checkbox } from "@/components/shadcn/checkbox";
import { Skeleton } from "@/components/shadcn/skeleton";
import { cn } from "@/lib/utils";

export const deletedHistoryAnalyticsCopy = (kind: "runs" | "chats") => (
  <>
    Workspace analytics for the deleted {kind} are lost.{" "}
    <strong className="font-medium">Billing is unaffected</strong>.
  </>
);

const blockClassName =
  "mt-3 flex items-start gap-2.5 rounded-lg border border-border bg-muted/40 p-3";

export const DeleteUsageHistoryOption = ({
  checked,
  onCheckedChange,
  count,
  isLoadingCount,
  entryLabel,
  isDisabled,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  count?: number;
  isLoadingCount?: boolean;
  // Plural noun for the history entries themselves: "runs", "conversations".
  entryLabel: "runs" | "chats";
  isDisabled?: boolean;
}) => {
  const singularEntry = entryLabel.replace(/s$/, "");

  // The states all keep the same block so the dialog never changes height as
  // the count arrives. A count that never arrives (the query failed) still
  // gets the checkbox, just without the tally — the purge is what matters.
  if (isLoadingCount) {
    return (
      <div className={blockClassName} aria-hidden>
        <Skeleton className="mt-0.5 size-4 shrink-0 rounded-[4px]" />
        <span className="flex min-w-0 flex-1 flex-col gap-2">
          <Skeleton className="h-3.5 w-56" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-4/5" />
        </span>
      </div>
    );
  }

  if (count === 0) {
    return (
      <div className={cn(blockClassName, "items-center")}>
        <span className="text-sm text-muted-foreground">
          No usage history to delete.
        </span>
      </div>
    );
  }

  return (
    <label className={cn(blockClassName, "cursor-pointer")}>
      <Checkbox
        checked={checked}
        onCheckedChange={(value) => onCheckedChange(value === true)}
        disabled={isDisabled}
        className="mt-0.5"
      />
      <span className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-foreground">
          Also delete its usage history
          {count !== undefined && (
            <span className="text-muted-foreground">
              {" "}
              ({count.toLocaleString()}{" "}
              {count === 1 ? singularEntry : entryLabel})
            </span>
          )}
        </span>
        <span className="text-xs leading-snug text-muted-foreground">
          This will permanently remove all {singularEntry} history and free up
          storage. {deletedHistoryAnalyticsCopy(entryLabel)} This action cannot
          be undone.
        </span>
      </span>
    </label>
  );
};
